/**
 * api.js — single axios instance for all backend calls.
 *
 * Purpose:
 *   • One place to configure `baseURL`, `withCredentials`, interceptors.
 *   • Every service module in `src/services/` builds on top of this.
 *   • Bounded request timeout so a hung backend doesn't freeze the UI
 *     forever — the response interceptor surfaces failures as toasts.
 *
 * Do NOT:
 *   • Import axios directly from components — use a service function.
 *   • Hardcode URLs — read REACT_APP_BACKEND_URL from env only.
 *
 * Per-call escape hatches:
 *   • Pass `{ suppressErrorToast: true }` in the axios config to opt out
 *     of the global toast (e.g. login 401, where the form shows the
 *     error inline). The original promise rejection still propagates.
 *   • Pass `{ timeout: 30000 }` to override the default per request.
 */
import axios from "axios";
import { toast } from "@/components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const API_BASE = `${BACKEND_URL}/api`;

// 15s is long enough for cold-cache Mongo reads and short enough that a
// stuck request gives up before the user does.
const DEFAULT_TIMEOUT_MS = 15_000;

const IS_DEV = process.env.NODE_ENV !== "production";

// FastAPI ships structured error payloads (`{error, detail, ...}`,
// `{error, expected, got}` for V-5/V-6 CAS conflicts, etc.). Toast and
// React both render strings only — passing the object crashes the
// Toaster. In dev we surface the full shape so debugging is fast; in
// prod we pick the most readable single field and hide the rest so end
// users don't see internals.
export function formatDetail(d) {
  if (d == null) return "";
  if (typeof d === "string") return d;
  if (typeof d === "number" || typeof d === "boolean") return String(d);
  if (IS_DEV) {
    try { return JSON.stringify(d); } catch { return String(d); }
  }
  // Prod: pick the most user-meaningful field, in priority order, then
  // fall through to a clean generic. Never leak the full payload.
  return d.detail || d.message || d.error || "Something went wrong.";
}

export const apiClient = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  timeout: DEFAULT_TIMEOUT_MS,
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const cfg = error?.config || {};
    if (cfg.suppressErrorToast) {
      return Promise.reject(error);
    }

    const status = error?.response?.status;
    const code = error?.code;
    const rawDetail = error?.response?.data?.detail ?? error?.response?.data?.error;
    // FastAPI structured errors (V-5/V-6 CAS conflict, InvalidActionError,
    // etc.) come back as objects like `{error, expected, got}`. Toast +
    // React render strings only — passing an object crashes the Toaster
    // ("Objects are not valid as a React child").
    //
    // Dev: stringify so the full payload is visible while debugging.
    // Prod: extract the most helpful single string field, or fall back
    // to a generic message rather than dumping JSON to end users.
    const detail = formatDetail(rawDetail);

    if (code === "ECONNABORTED" || /timeout/i.test(error?.message || "")) {
      toast.error("Request timed out", {
        description: "The server didn't respond in time. Check your connection and try again.",
      });
    } else if (!error?.response) {
      toast.error("Couldn't reach the server", {
        description: "Network error. We'll retry automatically when you act again.",
      });
    } else if (status === 401) {
      // Auth flows surface 401 inline (login form etc.) and should set
      // `suppressErrorToast`. Anything else gets a generic nudge.
      toast.error("Sign-in required", {
        description: detail || "Please log in to continue.",
      });
    } else if (status === 403) {
      toast.error("Not allowed", { description: detail || "You don't have access to that." });
    } else if (status === 429) {
      const retryAfter = error?.response?.headers?.["retry-after"];
      const hint = retryAfter ? ` Try again in ~${retryAfter}s.` : " Please slow down.";
      toast.error("Too many requests", { description: `Rate limit hit.${hint}` });
    } else if (status >= 500) {
      toast.error("Server error", {
        description: detail || "Something broke on our end. We've been notified.",
      });
    } else if (status >= 400 && detail) {
      toast.error(detail);
    }
    return Promise.reject(error);
  },
);

export default apiClient;
